if (!customElements.get('custom-select')) {
  class CustomSelect extends HTMLElement {
    constructor() {
      super();
      this.button = this.querySelector('.custom-select__btn');
      this.listbox = this.querySelector('.custom-select__listbox');
      this.selectedOption = this.querySelector('[aria-selected="true"]');

      // Set the selected option.
      if (!this.selectedOption) {
        this.selectedOption = this.listbox.firstElementChild;
      }

      this.setButtonWidth();
  // Eager init (was lazy via IntersectionObserver) so dropdowns always work even if off-screen initially
  this.init();
  try { console.debug('[custom-select] constructed', this.id); } catch(e) {}
    }

    init() {
      this.options = this.querySelectorAll('.custom-select__option');
      this.nativeSelect = document.getElementById(`${this.id}-native`);
      this.swatches = !!Array.from(this.options).find((el) => 'swatch' in el.dataset);
      this.focusedClass = 'is-focused';
      this.searchString = '';
      this.listboxOpen = false;
      this.selectedOption = this.querySelector('[aria-selected="true"]');

      // Set the selected option.
      if (!this.selectedOption) {
        this.selectedOption = this.listbox.firstElementChild;
      }

      this.addEventListener('keydown', this.handleKeydown.bind(this));
      this.button.addEventListener('mousedown', this.handleMousedown.bind(this));
    }

    /**
     * Returns the next visible sibling element
     * @param {Element} elem - The element to search
     * @returns {Element}
     */
    static getNextVisibleSibling(elem) {
      let sibling = elem.nextElementSibling;
      while (sibling) {
        if (sibling.offsetParent) return sibling;
        sibling = sibling.nextElementSibling;
      }
      return null;
    }

    /**
     * Returns the previous visible sibling element
     * @param {Element} elem - The element to search
     * @returns {Element}
     */
    static getPreviousVisibleSibling(elem) {
      let sibling = elem.previousElementSibling;
      while (sibling) {
        if (sibling.offsetParent) return sibling;
        sibling = sibling.previousElementSibling;
      }
      return null;
    }

    /**
     * Adds event listeners when the options list is open.
     */
    addListboxOpenListeners() {
      this.mouseoverHandler = this.handleMouseover.bind(this);
      this.mouseleaveHandler = this.handleMouseleave.bind(this);
      this.clickHandler = this.handleClick.bind(this);
  this.listbox.addEventListener('mouseover', this.mouseoverHandler);
  this.listbox.addEventListener('mouseleave', this.mouseleaveHandler);
  this.listbox.addEventListener('click', this.clickHandler);
    }

    /**
     * Removes event listeners added when the options list was open.
     */
    removeListboxOpenListeners() {
      this.listbox.removeEventListener('mouseover', this.mouseoverHandler);
      this.listbox.removeEventListener('mouseleave', this.mouseleaveHandler);
      this.listbox.removeEventListener('click', this.clickHandler);
    }

    /**
     * Handles 'keydown' events on the custom select element.
     * @param {object} evt - Event object.
     */
    handleKeydown(evt) {
      if (this.listboxOpen) {
        this.handleKeyboardNav(evt);
      } else if (evt.key === 'ArrowUp' || evt.key === 'ArrowDown' || evt.key === ' ') {
        evt.preventDefault();
        this.showListbox();
      }
    }

    /**
     * Handles 'mousedown' events on the button element.
     * @param {object} evt - Event object.
     */
    handleMousedown(evt) {
      // Original open-only behavior (toggle caused premature close issues)
      if (!this.listboxOpen && evt.button === 0) {
        this.showListbox();
      }
    }

    /**
     * Handles 'mouseover' events on the options list.
     * @param {object} evt - Event object.
     */
    handleMouseover(evt) {
      if (evt.target.matches('li')) {
        this.focusOption(evt.target);
      }
    }

    /**
     * Handles 'mouseleave' events on the options list.
     */
    handleMouseleave() {
      this.focusOption(this.selectedOption);
    }

    /**
     * Handles 'click' events on the custom select element.
     * @param {object} evt - Event object.
     */
    handleClick(evt) {
      // mouse down + move + up will trigger a click event on parent - only respond to clicks on LI
      if (evt.target.tagName === 'LI') {
        this.selectOption(evt.target);
      }
    }

    /**
     * Handles 'blur' events on the options list.
     */
    handleBlur() {
      if (this.listboxOpen) {
        this.hideListbox();
      }
    }

    /**
     * Handles 'keydown' events on the options list.
     * @param {object} evt - Event object.
     */
    handleKeyboardNav(evt) {
      let optionToFocus;

      // Disable tabbing if options list is open (as per native select element).
      if (evt.key === 'Tab') {
        evt.preventDefault();
      }

      switch (evt.key) {
        // Focus an option.
        case 'ArrowUp':
        case 'ArrowDown':
          evt.preventDefault();

          if (evt.key === 'ArrowUp') {
            optionToFocus = CustomSelect.getPreviousVisibleSibling(this.focusedOption);
          } else {
            optionToFocus = CustomSelect.getNextVisibleSibling(this.focusedOption);
          }

          if (optionToFocus && !optionToFocus.classList.contains('is-disabled')) {
            this.focusOption(optionToFocus);
          }
          break;

        // Select an option.
        case 'Enter':
        case ' ':
          evt.preventDefault();
          this.selectOption(this.focusedOption);
          break;

        // Cancel and close the options list.
        case 'Escape':
          evt.preventDefault();
          this.hideListbox();
          break;

        // Search for an option and focus the first match (if one exists).
        default:
          optionToFocus = this.findOption(evt.key);

          if (optionToFocus) {
            this.focusOption(optionToFocus);
          }
          break;
      }
    }

    /**
     * Sets the button width to the same as the longest option, to prevent
     * the button width from changing depending on the option selected.
     */
    setButtonWidth() {
      // Get the width of an element without side padding.
      const getHorizontalPadding = (el) => {
        const elStyle = getComputedStyle(el);
        return parseFloat(elStyle.paddingLeft) + parseFloat(elStyle.paddingRight);
      };

      const buttonPadding = getHorizontalPadding(this.button);
      const optionPadding = getHorizontalPadding(this.selectedOption);
      const buttonBorder = this.button.offsetWidth - this.button.clientWidth;
      const optionWidth = Math.ceil(this.selectedOption.getBoundingClientRect().width);

      this.button.style.setProperty('--custom-select-button-width', `${optionWidth - optionPadding + buttonPadding + buttonBorder}px`);
    }

    /**
     * Determine if we need to portal the listbox to <body> due to a transformed ancestor.
     * Any ancestor with transform / perspective / filter / opacity<1 creates a new stacking context
     * that can cause a high z-index sibling (like the next custom-select) to appear above.
     */
    shouldPortal() {
      let n = this;
      while (n && n !== document.documentElement) {
        const cs = getComputedStyle(n);
        if (cs.transform !== 'none' || cs.perspective !== 'none' || cs.filter !== 'none' || parseFloat(cs.opacity) < 1) {
          return true;
        }
        n = n.parentElement;
      }
      return false;
    }

    /**
     * Shows the options list.
     */
  showListbox() {
  this._openedAt = performance.now();
  // DEBUG: force open without relying on hidden attribute
  this.listbox.hidden = false;
  this.listbox.removeAttribute('hidden');
  this.listboxOpen = true;

      this.classList.add('is-open');
      this.button.setAttribute('aria-expanded', 'true');
      this.listbox.setAttribute('aria-hidden', 'false');
      // Force visibility in case external CSS (or prior utility classes) keep it visually hidden
      Object.assign(this.listbox.style, {
        display: 'block',
        visibility: 'visible',
        opacity: '1'
      });
      // Remove legacy utility class if still present
      if (this.listbox.classList.contains('invisible')) {
        this.listbox.classList.remove('invisible');
      }
      // If listbox has utility class 'invisible' (from initial markup), remove it now that we're displaying

      // Close any other open custom selects & reset their z-index
      document.querySelectorAll('custom-select.is-open').forEach((openSel) => {
        if (openSel !== this && typeof openSel.hideListbox === 'function') {
          openSel.hideListbox();
        }
      });
      // Decide portal vs inline placement
      this._portalled = this.shouldPortal();
      if (this._portalled) {
        try {
          this._originalListboxParent = this.listbox.parentElement;
          const btnRect = this.button.getBoundingClientRect();
          this.listbox.classList.add('custom-select__listbox--portal');
          Object.assign(this.listbox.style, {
            position: 'absolute',
            top: Math.round(btnRect.bottom + window.scrollY) + 'px',
            bottom: 'auto',
            left: Math.round(btnRect.left + window.scrollX) + 'px',
            width: Math.round(btnRect.width) + 'px',
            minWidth: Math.round(btnRect.width) + 'px',
            maxHeight: 'min(60vh, 420px)',
            overflowY: 'auto',
            zIndex: 10000
          });
          document.body.appendChild(this.listbox);
          this._repositionPortal = () => {
            if (!this.listboxOpen) return;
            const r = this.button.getBoundingClientRect();
            this.listbox.style.top = Math.round(r.bottom + window.scrollY) + 'px';
            this.listbox.style.left = Math.round(r.left + window.scrollX) + 'px';
            this.listbox.style.width = Math.round(r.width) + 'px';
            this.listbox.style.minWidth = Math.round(r.width) + 'px';
          };
          window.addEventListener('scroll', this._repositionPortal, true);
          window.addEventListener('resize', this._repositionPortal, true);
        } catch(e) { /* ignore */ }
      } else {
        // INLINE POSITION (no portal) with controlled z-index stacking per open dropdown
        try {
          const container = this;
          if (container && getComputedStyle(container).position === 'static') {
            container.style.position = 'relative';
          }
          const BASE_Z = 4000; // Above typical headers/forms/add-to-cart (adjust if needed)
          if (container) container.style.zIndex = BASE_Z;
          // `--custom-select-direction: up` lets CSS force an upward listbox; otherwise flip when there's no room below.
          const listboxStyle = getComputedStyle(this.listbox);
          const cssOpensUpwards = listboxStyle.getPropertyValue('--custom-select-direction').trim() === 'up';
          const btnRect = this.button.getBoundingClientRect();
          const spaceBelow = window.innerHeight - btnRect.bottom;
          const openUpwards = cssOpensUpwards
            || (spaceBelow < Math.min(240, this.listbox.scrollHeight + 16) && btnRect.top > spaceBelow);
          Object.assign(this.listbox.style, {
            position: 'absolute',
            top: openUpwards ? 'auto' : this.button.offsetHeight + 'px',
            bottom: openUpwards ? this.button.offsetHeight + 'px' : 'auto',
            left: 0,
            minWidth: this.button.offsetWidth + 'px',
            width: this.button.offsetWidth + 'px',
            zIndex: BASE_Z + 1,
            maxHeight: 'min(60vh, 420px)',
            overflowY: 'auto'
          });
        } catch(e) { /* ignore */ }
      }

  // Minimal fallback still: ensure visible if zero-size after paint
      setTimeout(() => {
        try {
          const r = this.listbox.getBoundingClientRect();
          if ((r.width === 0 || r.height === 0) && this.listboxOpen) {
            this.listbox.style.display = 'block';
            this.listbox.style.visibility = 'visible';
            this.listbox.style.opacity = '1';
          }
        } catch(e) {}
      }, 60);

      // Slight delay required to prevent blur event being fired immediately.
      setTimeout(() => {
        this.focusOption(this.selectedOption);
        try { this.listbox.focus(); } catch(e) {}
        this.addListboxOpenListeners && this.addListboxOpenListeners();
        // Deferred outside click close (ignore opening mousedown)
        const openedAt = Date.now();
        const outsideHandler = (evt2) => {
          if (!this.listboxOpen) return;
          if (Date.now() - openedAt < 100) return; // guard against same click
          const t = evt2.target;
          if (this.contains(t) || this.listbox.contains(t)) return;
          this.hideListbox();
        };
        this._outsideClickTemp = outsideHandler;
        // Use click + touchstart only (pointerdown was interfering with open sequence)
        document.addEventListener('click', outsideHandler, true);
        document.addEventListener('touchstart', outsideHandler, true);
        // Close on focus moving elsewhere (keyboard tabbing)
        this._focusInHandler = (evt) => {
          if (!this.listboxOpen) return;
          if (this.contains(evt.target) || this.listbox.contains(evt.target)) return;
          this.hideListbox();
        };
        document.addEventListener('focusin', this._focusInHandler, true);
        // Global keydown (escape already handled locally, tab outside when button loses focus)
        this._globalKeyHandler = (evt) => {
          if (!this.listboxOpen) return;
          if (evt.key === 'Tab') {
            // Allow natural tab, then close immediately after stack clears
            setTimeout(() => { this.hideListbox(); }, 0);
          }
        };
        document.addEventListener('keydown', this._globalKeyHandler, true);
      });
    }

    /**
     * Hides the options list.
     */
    hideListbox() {
      if (!this.listboxOpen) return;
  const elapsed = performance.now() - (this._openedAt || 0);

  this.listbox.hidden = true;
      this.listboxOpen = false;

      this.classList.remove('is-open');
      // Reset container z-index (inline mode only)
      if (!this._portalled) {
        try {
          this.style.zIndex = '';
        } catch(e) {}
      }
      this.button.setAttribute('aria-expanded', 'false');
      this.listbox.setAttribute('aria-hidden', 'true');
  // Clean inline overrides so closed state does not occupy layout
  this.listbox.style.display = 'none';
  this.listbox.style.opacity = '';
  this.listbox.style.visibility = '';
  this.listbox.style.top = '';
  this.listbox.style.bottom = '';
      // Re-add invisible utility class so CSS transitions/initial state remain consistent
  // We no longer rely on a utility 'invisible' class; aria-hidden + hidden attribute control visibility

      if (this.focusedOption) {
        this.focusedOption.classList.remove(this.focusedClass);
        this.focusedOption = null;
      }

      this.button.focus();
      this.removeListboxOpenListeners();

      if (this._outsideClickTemp) {
        document.removeEventListener('click', this._outsideClickTemp, true);
        document.removeEventListener('touchstart', this._outsideClickTemp, true);
        this._outsideClickTemp = null;
      }
      if (this._focusInHandler) { document.removeEventListener('focusin', this._focusInHandler, true); this._focusInHandler = null; }
      if (this._globalKeyHandler) { document.removeEventListener('keydown', this._globalKeyHandler, true); this._globalKeyHandler = null; }
      // Portal cleanup
      if (this._portalled) {
        try {
          window.removeEventListener('scroll', this._repositionPortal, true);
          window.removeEventListener('resize', this._repositionPortal, true);
          if (this._originalListboxParent) {
            this._originalListboxParent.appendChild(this.listbox);
          }
          this.listbox.classList.remove('custom-select__listbox--portal');
        } catch(e) {}
        this._portalled = false;
      }
    }

    /**
     * Finds a matching option from a typed string.
     * @param {string} key - Key pressed.
     * @returns {?Element}
     */
    findOption(key) {
      this.searchString += key;

      // If there's a timer already running, clear it.
      if (this.searchTimer) {
        clearTimeout(this.searchTimer);
      }

      // Wait 500ms to see if another key is pressed, if not then clear the search string.
      this.searchTimer = setTimeout(() => {
        this.searchString = '';
      }, 500);

      // Find an option that contains the search string (if there is one).
      const matchingOption = Array.from(this.options).find((option) => {
        const label = option.innerText.toLowerCase();
        return label.includes(this.searchString) && !option.classList.contains('is-disabled');
      });

      return matchingOption;
    }

    /**
     * Focuses an option.
     * @param {Element} option - <li> element of the option to focus.
     */
    focusOption(option) {
      // Remove focus on currently focused option (if there is one).
      if (this.focusedOption) {
        this.focusedOption.classList.remove(this.focusedClass);
      }

      // Set focus on the option.
      this.focusedOption = option;
      this.focusedOption.classList.add(this.focusedClass);

      // If option is out of view, scroll the list.
      if (this.listbox.scrollHeight > this.listbox.clientHeight) {
        const scrollBottom = this.listbox.clientHeight + this.listbox.scrollTop;
        const optionBottom = option.offsetTop + option.offsetHeight;

        if (optionBottom > scrollBottom) {
          this.listbox.scrollTop = optionBottom - this.listbox.clientHeight;
        } else if (option.offsetTop < this.listbox.scrollTop) {
          this.listbox.scrollTop = option.offsetTop;
        }
      }
    }

    /**
     * Selects an option.
     * @param {Element} option - Option <li> element.
     */
    selectOption(option) {
      if (option !== this.selectedOption) {
        // Switch aria-selected attribute to selected option.
        option.setAttribute('aria-selected', 'true');
        this.selectedOption.setAttribute('aria-selected', 'false');

        // Update swatch colour in the button.
        if (this.swatches) {
          this.button.dataset.swatch = option.dataset.swatch || '';
          const nativeSwatchColor = option.style.getPropertyValue('--native-swatch-color');
          if (nativeSwatchColor) {
            this.button.style.setProperty('--native-swatch-color', nativeSwatchColor);
            this.button.style.setProperty('--native-swatch-image', '');
          }
          const nativeSwatchImage = option.style.getPropertyValue('--native-swatch-image');
          if (nativeSwatchImage) {
            this.button.style.setProperty('--native-swatch-image', nativeSwatchImage);
            this.button.style.setProperty('--native-swatch-color', '');
          }
        }

        // Update the button text and set the option as active.
        this.button.firstElementChild.textContent = option.firstElementChild.textContent;
        this.listbox.setAttribute('aria-activedescendant', option.id);
        this.selectedOption = document.getElementById(option.id);

        // If a native <select> exists, update its selected value and dispatch a 'change' event.
        if (this.nativeSelect) {
          this.nativeSelect.value = option.dataset.value;
          this.nativeSelect.dispatchEvent(new Event('change', { bubbles: true }));
        } else {
          // Dispatch a 'change' event on the custom select element.
          const detail = { selectedValue: option.dataset.value };
          this.dispatchEvent(new CustomEvent('change', { bubbles: true, detail }));
        }
      }

      this.hideListbox();
    }
  }

  customElements.define('custom-select', CustomSelect);
  // Safety: force-init any existing elements (edge cases)
  try {
    document.querySelectorAll('custom-select').forEach((el) => {
      if (!el.button || !el.listbox) {
        el.button = el.querySelector('.custom-select__btn');
        el.listbox = el.querySelector('.custom-select__listbox');
      }
      if (typeof el.init === 'function' && !el.button._csBound) {
        el.init();
        el.button._csBound = true;
      }
    });
  } catch(e) {}
}
